/**
 * ViewToggle — segmented control to switch between Freeform and Structured
 * views of the motion prompt.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'

export type ViewMode = 'freeform' | 'structured'

interface ViewToggleProps {
  mode: ViewMode
  onChange: (mode: ViewMode) => void
  disabled?: boolean
  disabledTooltip?: string
  nodeId?: string
}

export const ViewToggle = memo(function ViewToggle({
  mode,
  onChange,
  disabled = false,
  disabledTooltip,
  nodeId,
}: ViewToggleProps) {
  const { t } = useTranslation('canvas')
  const baseClass =
    'px-2 py-0.5 text-xxs uppercase tracking-wider transition-colors rounded'
  const selectedClass = 'bg-primary/20 text-primary'
  const unselectedClass = 'text-text-muted hover:text-foreground'
  const disabledClass = 'opacity-50 cursor-not-allowed'

  return (
    <div
      className="inline-flex items-center gap-0.5 bg-surface-1 border border-border-subtle rounded p-0.5"
      data-testid="av-view-toggle"
      data-nux-anchor="video-generator-modes"
      data-nux-node-id={nodeId}
      data-nux-node-type="videoGenerator"
      title={disabled ? disabledTooltip : undefined}
    >
      <button
        type="button"
        onClick={() => onChange('freeform')}
        className={`${baseClass} ${mode === 'freeform' ? selectedClass : unselectedClass} ${
          disabled && mode !== 'freeform' ? disabledClass : ''
        }`}
        data-testid="av-view-toggle-freeform"
      >
        {t('viewToggle.freeform')}
      </button>
      <button
        type="button"
        onClick={() => !disabled && onChange('structured')}
        aria-disabled={disabled}
        title={disabled ? disabledTooltip : undefined}
        className={`${baseClass} ${mode === 'structured' ? selectedClass : unselectedClass} ${
          disabled ? disabledClass : ''
        }`}
        data-testid="av-view-toggle-structured"
      >
        {t('viewToggle.structured')}
      </button>
    </div>
  )
})
